import { useCallback, useContext, useMemo } from 'react';

import { SceneSetParams, useSafeParams } from 'components/Routes/useSafeParams';

import { EphemeralStateContext } from './EphemeralStateProvider';
import { getStateKeyFromIdentifiers } from './helpers';

interface SetValue<T> {
  (arg: T): void;
}

export function useEphemeralState<Q extends number, D>(
  questionId: Q,
  defaultValue: D | null
): [D, SetValue<D>] {
  const { setValuesByKey, getValuesByKey } = useContext(EphemeralStateContext);
  const { sceneSetGraphId, sceneSetId } = useSafeParams<SceneSetParams>();

  const stateKey = getStateKeyFromIdentifiers({
    sceneSetGraphId,
    sceneSetId,
    questionId,
  });
  const values: D = useMemo(() => getValuesByKey(stateKey) || defaultValue, [
    defaultValue,
    getValuesByKey,
    stateKey,
  ]);
  const setValues: SetValue<D> = useCallback(
    value => setValuesByKey(stateKey, value),
    [setValuesByKey, stateKey]
  );
  return [values, setValues];
}
